import { SearchableSelect } from "@/components/ui/searchable-select";
import { AddUser } from "@/components/users/add-user.tsx";
import { useGroups } from "@/lib/api/group";
import { useRoles } from "@/lib/api/roles";
import { useDebounce } from "@/lib/hooks/use-debounce.ts";
import { formatRoleLabel } from "@/lib/utils/role-label";
import { ROLE_PRECEDENCE } from "@/lib/utils/system-roles";
import { cn } from "@/lib/utils";
import { useRouter, useSearch } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

const ALL_ROLES = "all";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Deactivated" },
  { value: "any", label: "All" },
];

const roleSortIndex = (name: string) => {
  const index = ROLE_PRECEDENCE.indexOf(
    name as (typeof ROLE_PRECEDENCE)[number],
  );
  return index === -1 ? ROLE_PRECEDENCE.length : index;
};

export function Filters() {
  const {
    search: urlSearch,
    assignedRole: urlAssignedRole,
    group: urlGroup,
    status: urlStatus,
  } = useSearch({
    strict: false,
  }) as Record<string, string>;

  const router = useRouter();
  const [search, setSearch] = useState(urlSearch || "");
  const assignedRole = urlAssignedRole || ALL_ROLES;
  const status = urlStatus || "active";
  const { data: groups } = useGroups();
  const { data: roles } = useRoles();

  const roleFilters = useMemo(
    () => [
      { label: "All roles", value: ALL_ROLES },
      ...(roles ?? [])
        .slice()
        .sort((a, b) => {
          const rank = roleSortIndex(a.name) - roleSortIndex(b.name);
          return rank !== 0 ? rank : a.name.localeCompare(b.name);
        })
        .map((role) => ({
          label: formatRoleLabel(role.name) ?? role.name,
          value: role.name,
        })),
    ],
    [roles],
  );

  const debouncedSearch = useDebounce(search, 500);
  const isFirstSearchSync = useRef(true);

  useEffect(() => {
    if (isFirstSearchSync.current) {
      isFirstSearchSync.current = false;
      return;
    }
    const trimmedSearch = debouncedSearch.trim();
    router.navigate({
      search: ((old: Record<string, unknown>) => ({
        ...old,
        search: trimmedSearch || undefined,
        page: 1,
      })) as never,
    });
  }, [debouncedSearch, router]);

  const setSearchParam = (patch: Record<string, unknown>) => {
    router.navigate({
      search: ((old: Record<string, unknown>) => ({
        ...old,
        ...patch,
        page: 1,
      })) as never,
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-md">
      <label className="flex h-12 min-w-[220px] flex-1 items-center gap-2.5 rounded-xl border border-buttonBorder-tertiary bg-surfaces-z0 px-3.5">
        <Search className="size-lg shrink-0 text-icon-tertiary" aria-hidden />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onBlur={() => setSearch(search.trim())}
          placeholder="Search"
          className="w-full bg-transparent font-body text-body-2 text-text-primary outline-none placeholder:text-text-placeholder"
        />
      </label>
      <div
        className="flex h-12 shrink-0 items-center rounded-full bg-surfaces-z4 p-xs"
        role="group"
        aria-label="Status"
      >
        {STATUS_OPTIONS.map((option) => {
          const active = status === option.value;
          return (
            <button
              key={option.value}
              type="button"
              aria-pressed={active}
              onClick={() => setSearchParam({ status: option.value })}
              className={cn(
                "flex h-10 items-center rounded-full px-lg font-body text-sm font-bold",
                active
                  ? "bg-surfaces-z0 text-text-primary shadow-low"
                  : "text-text-secondary",
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
      <SearchableSelect
        aria-label="Role"
        searchPlaceholder="Search role"
        value={assignedRole}
        options={roleFilters}
        onChange={(value) =>
          setSearchParam({
            assignedRole: value === ALL_ROLES ? undefined : value,
          })
        }
        className="w-[180px]"
      />
      <SearchableSelect
        aria-label="Group"
        searchPlaceholder="Search group"
        value={urlGroup || "all"}
        options={[
          { value: "all", label: "All groups" },
          ...(groups ?? []).map((group) => ({
            value: group.id,
            label: group.name,
          })),
        ]}
        onChange={(value) => setSearchParam({ group: value })}
        className="w-[200px]"
      />
      <AddUser />
    </div>
  );
}
